import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import "flag-icons/css/flag-icons.min.css";
import Slider from "react-slick";
import SpotifyIcon from '../../../assets/images/admin/Spotify.svg';
import YouTubeIcon from '../../../assets/images/admin/Youtube.png';
import AppleIcon from '../../../assets/images/admin/Apple.svg';
import SoundCloudIcon from '../../../assets/images/admin/soundcloud-icon.png';
import AddClickIcon from '../../../assets/images/admin/add-clicks-icon.png'
import PlaylistClickIcon from '../../../assets/images/admin/playlist-icons.png'
import CustomlinkIcon from '../../../assets/images/icons/customLink.png'
import MerchTshirtIcon from '../../../assets/images/icons/merch-tshirt.png'
import SellTicketIcon from '../../../assets/images/icons/sell-ticket.png'
import { getGoalIconAndName } from '../../../helper/utility';
import ChatInput from '../AmplitudeAI/Chat/ChatInput';
import { PricingPlanModal } from '../../../components/PricingPlanModal';
import { CreditPlanModal } from '../../../components/CreditPlanModal';
import AppTour from '../../../components/AppTour/AppTour';
import { campaignEditorTourSteps } from '../../../components/AppTour/ModuleTourSteps';

// Demo page using the exact UI of CampaignEditor with mocked data
export default function CampaignEditorDemo() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { artistInfo } = useSelector(state => state?.artistDetails || {});
    const { hasActiveSubscription, creditDetails } = useSelector((state) => state.billing || {});

    const [campaignDetails, setCampaignDeails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showBudgetModal, setShowBudgetModal] = useState(false);
    const [openFor, setOpenFor] = useState('');
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [showPricingPlanModal, setShowPricingPlanModal] = useState(false);
    const [showCreditPlanModal, setShowCreditPlanModal] = useState(false);

    const platformIcons = {
        spotify: SpotifyIcon,
        apple: AppleIcon,
        youtube: YouTubeIcon,
        soundcloud: SoundCloudIcon,
    };

    const [runTour, setRunTour] = useState(false);

    useEffect(() => {
        if (window.location.hash === '#start-tour') setRunTour(true);
    }, []);

    useEffect(() => {
        // Set up dummy campaign details to show full UI
        const dummy = {
            campaign: {
                id: 'demo-campaign-1',
                name: 'Demo Campaign: Grow Streams',
                effective_status: 'ACTIVE',
                start_time: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
                stop_time: new Date(Date.now() + 25 * 24 * 60 * 60 * 1000).toISOString(),
                totalBudget: 300,
                targetObject: {
                    targetType: 'song',
                    trackName: 'Demo Track',
                    songImage: SpotifyIcon,
                    instaUserName: 'demo_artist',
                    targetName: 'Link Promotion',
                    targetObject: {},
                },
                instagramInfo: { username: 'demo_insta', id: 'insta-1', profile_picture_url: SpotifyIcon },
                pageInfo: { name: 'Demo Page', id: 'page-1' },
            },
            insights: { spend: 123.45, reach: 5000, impressions: 20000, cpm: 6.15, clicks: 432, cpc: 0.28 },
            actions: { post_reaction: 120, link_click: 432, comment: 8 },
            accountInfo: { currency: '$', id: 'acct-1', name: 'Demo Account' },
            getAdSets: [
                {
                    budgetPercentage: 60,
                    budgetType: 'Daily',
                    targeting: {
                        location: { countries: [{ name: 'India', id: 'IN' }], regions: [], cities: [] },
                        languages: ['en'],
                        interests: ['music'],
                        gender: 'All',
                        ageRange: { min: 18, max: 45 }
                    }
                },
                {
                    budgetPercentage: 40,
                    targeting: {
                        location: { countries: [{ name: 'United States', id: 'US' }], regions: [], cities: [] },
                    }
                }
            ],
            creativeDetails: [
                {
                    mediaType: 'Image',
                    media: { url: require('../../../assets/images/admin/social-img7.png') },
                    headline: 'Demo Headline',
                    description: 'This is a demo creative used in the CampaignEditor demo page.',
                    callToAction: 'Listen Now',
                    destinationUrl: 'https://spotify.com/demo'
                }
            ],
            smartLinkAnalytics: { totals: { clicksToService: 120 }, streams: [{ name: 'spotify', clicksToService: 80 }, { name: 'apple', clicksToService: 40 }] },
            platformClicksData: [
                { publisherPlatform: 'instagram', position: 'feed', clicks: 200 },
                { publisherPlatform: 'instagram', position: 'instagram_reels', clicks: 120 },
                { publisherPlatform: 'facebook', position: 'facebook_stories', clicks: 112 }
            ],
            ageGenderBreakdownData: [
                { age: '18-24', gender: 'female', clicks: 160 },
                { age: '25-34', gender: 'male', clicks: 120 },
                { age: '35-44', gender: 'female', clicks: 80 }
            ],
            geoBreakdown: [
                { country: 'IN', clicks: 220 },
                { country: 'US', clicks: 120 },
                { country: 'GB', clicks: 60 }
            ]
        };

        setCampaignDeails(dummy);
        setLoading(false);
    }, [dispatch]);

    const goalMatch = getGoalIconAndName(campaignDetails?.campaign?.name, []);

    const getDemographicProgress = (total, value) => {
        return Math.min((Number(value || 0).toFixed(2) / Number(total || 1).toFixed(2)) * 100, 100).toFixed(2) || 0;
    }

    const reelsClicks = campaignDetails?.platformClicksData?.find(p => p.position === 'instagram_reels')?.clicks || 0;
    const storiesClicks = campaignDetails?.platformClicksData?.find(p => p.position === 'facebook_stories')?.clicks || 0;
    const igPostsClicks = campaignDetails?.platformClicksData?.find(p => p.position === 'feed')?.clicks || 0;

    const handleSend = (input) => {
        // keep same behavior as original: open pricing/credit modals when needed
        if (!hasActiveSubscription) {
            setShowPricingPlanModal(true);
            return;
        }
        if (!creditDetails?.data || creditDetails?.data?.remaining <= 0) {
            setShowCreditPlanModal(true);
            return;
        }
        navigate('/admin/amplitude-ai');
    };

    return (
        <div className='CampaignEditor-page py-5' data-tour="campaign-editor-demo">
            <AppTour tourSteps={campaignEditorTourSteps} runTour={runTour} setRunTour={setRunTour} />

            <div className="container max-w-full">
                <div className="card bg-gradient-to-r from-theme_clr1 to-theme_clr2 rounded-xl mb-4 p-4 lg:p-5" data-tour="campaign-header">
                    <div className='flex flex-wrap items-center justify-between gap-[11px]'>
                        <h5 className='flex items-center text-white text-[23px] font-manrope font-bold leading-tight'>Campaign Editor <i className="bi bi-bar-chart-line-fill text-4xl leading-none ms-8"></i></h5>
                        <button className='btn-custom2 w-[192px] flex items-center justify-center gap-5 shadow-lg' onClick={() => navigate('/admin/growth-engine/campaign')}>New Campaign <i className='bi bi-plus-circle text-[20px]'></i></button>
                    </div>
                </div>

                <div className="info-wrapper bg-theme_clr1 rounded-lg p-4">
                    <div className="item-info flex flex-col sm:flex-row sm:flex-wrap gap-4 flex-1 bg-white rounded-md mb-4 p-3" data-tour='campaign-banner'>
                        <span className="w-[110px] h-[110px] bg-theme_clr1 flex items-center justify-center rounded-2xl object-cover">
                            {loading ? (
                                <div className="w-full h-full bg-gray-200 rounded-2xl animate-pulse"></div>
                            ) : (
                                <img src={campaignDetails?.campaign?.targetObject?.songImage || SpotifyIcon} alt="Spotify Icon" className="rounded-2xl" />
                            )}
                        </span>
                        <div className="info-wrapper flex-1 flex flex-col lg:flex-row lg:flex-wrap lg:items-center justify-between gap-x-8 gap-y-3">
                            <div className="content-wrapper">
                                {loading ? (
                                    <div className="h-6 w-40 bg-gray-200 rounded-md animate-pulse mb-2"></div>
                                ) : (
                                    <div className="text-theme_clr1 text-[20px] font-medium mb-1">
                                        {campaignDetails?.campaign?.targetObject?.trackName || 'Overview'}
                                    </div>
                                )}
                                {loading ? (
                                    <div className="h-4 w-32 bg-gray-200 rounded-md animate-pulse mb-3"></div>
                                ) : (
                                    <div className="description text-theme_clr1 font-inter mb-3">
                                        {campaignDetails?.campaign?.targetObject?.artistName || artistInfo?.name || 'Demo Artist'}
                                    </div>
                                )}

                                <div className="btn-wrapper flex flex-wrap items-center gap-x-4 gap-y-3">
                                    {!loading && (
                                        <>
                                            <button className="bg-theme_clr1 text-white text-[14px] font-inter font-medium flex items-center gap-1 rounded-[4px] py-2 px-4">
                                                {goalMatch?.name || 'More Streams'}
                                            </button>
                                            <button className={`bg-green-400 text-green-800 text-[14px] min-w-[80px] font-inter font-medium flex items-center justify-center gap-1 rounded-3xl uppercase py-2 px-4`}>
                                                Live
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="actions" data-tour='campaign-actions'>
                                <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                                    <button className="text-theme_clr1 font-semibold flex items-center gap-2"><i className="bi bi-copy"></i> Duplicate</button>
                                    <button className="text-theme_clr1 font-semibold flex items-center gap-2"><i className="bi bi-pencil"></i> Rename</button>
                                    <button className="text-theme_clr1 font-semibold flex items-center gap-2"><i className="bi bi-pause-fill"></i> Pause</button>
                                    <button className="text-theme_clr1 font-semibold flex items-center gap-2"><i className="bi bi-cash-coin"></i> Edit Budget</button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-[1fr_350px] gap-4">
                        <div className="left-sec">
                            <div className="bg-white rounded-lg p-4">
                                <div className='wrapper flex flex-wrap mb-4' data-tour="amplitude-ai-chat">
                                    <ChatInput
                                        title={`Launch campaigns to grow your fanbase.`}
                                        inputPlaceholder={'Use Amplitude in crafting the perfect campaign based on your vision.'}
                                        onSend={handleSend}
                                        disabled={false}
                                    />
                                </div>
                                <div className="flex flex-col lg:flex-row justify-between gap-3 mb-4" data-tour="performance-section">
                                    <div className="info-card flex-1 border rounded-lg p-4" data-tour='campaign-budget'>
                                        <div className="mb-2">
                                            <div className="text-black text-lg font-semibold">Budget</div>
                                            <div className="text-[#6B7280] text-sm">{campaignDetails?.accountInfo?.currency}{campaignDetails?.campaign?.totalBudget?.toFixed(2)}</div>
                                        </div>
                                        <div className='relative w-100 h-2 bg-gray-200 overflow-hidden rounded-xl mb-2'>
                                            <div className='h-full bg-theme_clr2' style={{ width: `40%` }} />
                                        </div>
                                        <div className="flex items-center gap-1"><span className="text-[#6B7280] text-base">Spent: </span>{campaignDetails?.accountInfo?.currency} {Number(campaignDetails?.insights?.spend || 0).toFixed(2)}</div>
                                    </div>
                                    <div className="info-card flex-1 border rounded-lg p-4" data-tour='campaign-length'>
                                        <div className="mb-2">
                                            <div className="text-black text-lg font-semibold">Length</div>
                                            <div className="text-[#6B7280] text-sm">{new Date(campaignDetails?.campaign?.stop_time).toLocaleDateString()}</div>
                                        </div>
                                        <div className='relative w-100 h-2 bg-gray-200 overflow-hidden rounded-xl mb-2'>
                                            <div className='h-full bg-theme_clr2' style={{ width: `25%` }} />
                                        </div>
                                    </div>
                                </div>

                                <div className="info-card grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[2.5fr_1fr_1fr] gap-x-2 gap-y-3 border rounded-lg mb-4 p-4" data-tour='performance-overview'>
                                    <div className="info-box flex gap-2 sm:col-span-2 lg:col-span-1">
                                        <span className="w-[45px] h-[45px] min-w-[45px] flex items-center justify-center rounded-lg object-cover">
                                            <img src={AddClickIcon} alt="Spotify Icon" style={{ width: '45px' }} />
                                        </span>
                                        <div className="info-wrapper">
                                            <div className="label text-black text-base font-semibold">Ad Clicks</div>
                                            <div className="value text-sm text-[#6B7280]">The number of people who clicked your ad link.</div>
                                        </div>
                                    </div>
                                    <div className="info-box">
                                        <div className="label text-sm text-[#6B7280]">Clicks</div>
                                        <div className="value text-black text-lg font-medium">{campaignDetails?.insights?.clicks || 0}</div>
                                    </div>
                                    <div className="info-box">
                                        <div className="label text-sm text-[#6B7280]">Cost-per-Clicks</div>
                                        <div className="value text-black text-lg font-medium">{campaignDetails?.accountInfo?.currency} {Number(campaignDetails?.insights?.cpc || 0)}</div>
                                    </div>
                                </div>

                                <div className="grid-wrapper grid grid-cols-[1fr_1fr] lg:grid-cols-[1fr_1fr_1fr] xl:grid-cols-[1fr_1fr_1fr_1fr] gap-3 mb-4" data-tour='performance-stats-tiles'>
                                    {[{ key: 'Total Spent', value: campaignDetails?.insights?.spend }, { key: 'Reach', value: campaignDetails?.insights?.reach }, { key: 'Impressions', value: campaignDetails?.insights?.impressions }, { key: 'CPM', value: campaignDetails?.insights?.cpm }].map((data, index) => (
                                        <div className="grid-item border rounded-lg p-3 sm:p-4" key={index}>
                                            <span className="w-[45px] h-[45px] min-w-[45px] flex items-center justify-center rounded-lg object-cover mb-2">
                                                <img src={AddClickIcon} alt="Spotify Icon" style={{ width: '45px' }} />
                                            </span>
                                            <div className="value text-sm text-[#6B7280] mb-1">{data.key}</div>
                                            <div className="label text-black text-base font-semibold">{data.value}</div>
                                        </div>
                                    ))}
                                </div>

                                <div className="grid-wrapper grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-4 mb-4" data-tour='primary-secondary-markets'>
                                    <div className="info-card bg-theme_clr1 rounded-lg p-4" data-tour="primary-market">
                                        <div className="flex flex-wrap items-center justify-between gap-2 font-medium mb-3">
                                            <span className="text-white">Primary Market</span>
                                            <span className="text-theme_clr2"><i className='bi bi-pencil-fill me-1'></i>{campaignDetails?.getAdSets[0]?.budgetPercentage || 100}% of budget</span>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {campaignDetails?.getAdSets[0]?.targeting?.location?.countries?.map((loc, idx) => (
                                                <span key={idx} className="badge border text-theme_clr2 border-theme_clr2 rounded-3xl capitalize px-3 py-1">{loc.name}</span>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="info-card bg-theme_clr1 rounded-lg p-4">
                                        <div className="flex flex-wrap items-center justify-between gap-2 font-medium mb-3">
                                            <span className="text-white">Secondary Markets</span>
                                        </div>
                                        <div className="flex justify-center items-center w-full py-6">
                                            <span className="text-gray-400 italic">No location in secondary market</span>
                                        </div>
                                    </div>
                                </div>

                                <hr className='my-4' />
                                <div className="info-wrapper" data-tour='streaming-clicks'>
                                    <div className="text-black text-lg font-semibold mb-1">Streaming Clicks by Platform</div>
                                    <div className="text-[#6B7280] text-base">These numbers show the clicks that converted to your chosen streaming services. They are typically lower than overall ad link clicks, since they depend on how many people clicked through your landing page.</div>
                                </div>

                                <hr className='my-4' />
                                <div className="grid-wrapper grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-8 mb-4">
                                    <div className="left-section" data-tour='top-demographics-clicks'>
                                        <div className="mb-4">
                                            <div className="text-black text-lg font-semibold">Top Demographics by Clicks</div>
                                            <div className="text-[#6B7280] text-base">Ads were most successful among demo audiences.</div>
                                        </div>
                                        <div className="progressbar-wrapper flex flex-col gap-3">
                                            {campaignDetails?.ageGenderBreakdownData?.map((item, index) => (
                                                <div key={index} className="progress-info flex flex-col sm:flex-row sm:items-center sm:gap-2">
                                                    <div className="sm:w-[80px] text-sm font-medium capitalize mb-1">{item.age} {item.gender}</div>
                                                    <div className="w-full bg-gray-200 rounded-md relative">
                                                        <div className="bg-theme_clr2 h-8 rounded-md text-nowrap py-1" style={{ width: `${getDemographicProgress(campaignDetails?.insights?.clicks || 1, item.clicks)}%` }}></div>
                                                        <div className="value text-sm font-medium text-body leading-none absolute top-1/2 -translate-y-1/2 end-4">{item.clicks} Clicks</div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="right-section" data-tour='top-country-clicks'>
                                        <div className="mb-4">
                                            <div className="text-black text-lg font-semibold">Top Country by Clicks</div>
                                        </div>
                                        <div className="progressbar-wrapper flex flex-col gap-3">
                                            {campaignDetails?.geoBreakdown?.map((item, idx) => (
                                                <div key={idx} className="progress-info flex items-center justify-between gap-4 md:gap-5 mt-[6px]">
                                                    <div className="text-2xl font-medium"><span className={`fi fi-${item.country.toLowerCase()}`}></span></div>
                                                    <div className="w-full bg-gray-200 rounded-md relative"><div className="bg-theme_clr2 h-8 rounded-md text-nowrap py-1" style={{ width: `${getDemographicProgress(campaignDetails?.insights?.clicks || 1, item.clicks)}%` }}></div><div className="value text-sm font-medium text-body leading-none absolute top-1/2 -translate-y-1/2 end-4">{item.clicks} Clicks</div></div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="right-sec">
                            <div className="bg-white rounded-lg mb-4 p-4" data-tour='creative-performance'>
                                <div className="text-black text-lg font-semibold mb-1">Creative Performance</div>
                                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                                    <div className="text-gray-500">By Clicks</div>
                                    <label htmlFor="uploadNewAssets" className="text-sm font-medium text-white bg-theme_clr4 rounded-3xl px-5 py-2"> Upload new assets
                                        <input type="file" accept='image/*' id="uploadNewAssets" className="hidden" />
                                    </label>
                                </div>
                                <div className="flex flex-wrap justify-between gap-2">
                                    <div className="info-box text-sm text-center"><div className="value font-semibold">{reelsClicks}</div><div className="label text-gray-500 capitalize">Reels</div></div>
                                    <div className="info-box text-sm text-center"><div className="value font-semibold">{storiesClicks}</div><div className="label text-gray-500 capitalize">Stories</div></div>
                                    <div className="info-box text-sm text-center"><div className="value font-semibold">{igPostsClicks}</div><div className="label text-gray-500 capitalize">IG Posts</div></div>
                                </div>
                            </div>

                            <div data-tour='creative-preview'>
                                {campaignDetails?.creativeDetails && campaignDetails.creativeDetails.length > 0 ? (
                                    <Slider dots={true} infinite={false} speed={500} slidesToShow={1} slidesToScroll={1} data-tour="creative-slider">
                                        {campaignDetails?.creativeDetails?.map((creative, index) => (
                                            <div key={index} className="bg-black text-white w-full h-[512px] relative rounded-lg overflow-hidden shadow-lg">
                                                {creative.mediaType === 'Image' && <img src={creative.media.url} alt="Creative Preview" className="absolute inset-0 w-full h-full object-cover" />}
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
                                                <div className="absolute bottom-4 left-4 right-4">
                                                    <div className="company-logo mb-3"><img src={require('../../../assets/images/logo.png')} alt="Logo" className="w-16" /></div>
                                                    <p className="text-gray-200 text-sm leading-snug line-clamp-2 mb-3">{creative.headline || ''}{creative.description || ''}</p>
                                                    <button className="w-full bg-gray-500 text-white text-base rounded-full font-semibold hover:bg-gray-400 transition px-4 py-2">{creative.callToAction || ''}</button>
                                                </div>
                                            </div>
                                        ))}
                                    </Slider>
                                ) : (
                                    <div className="bg-black text-white w-full h-[512px] rounded-lg flex items-center justify-center"><p className="text-lg font-semibold">No creatives available</p></div>
                                )}
                            </div>

                            <div className="bg-white rounded-lg p-4" data-tour="setup-details">
                                <div className="heading-wrapper mb-3"><div className="text-black text-lg font-semibold">Setup Details</div><div className="text-[#6B7280] text-base">Connections used for campaign</div></div>
                                <div className="cards-wrapper flex flex-col gap-3">
                                    <div className="box border rounded-lg">
                                        <div className="flex items-center gap-3 border-b px-4 py-2">
                                            <span className="bg-gray-200 h-9 w-9 flex items-center justify-center rounded-full"><i className="bi bi-link text-theme_clr4 text-xl"></i></span>
                                            <span className="text-base font-semibold">Promoted Link</span>
                                        </div>
                                        <div className="content p-4"><div className="label text-[#6B7280] text-base mb-1">URL</div><div className="description"><Link to={campaignDetails?.creativeDetails[0]?.destinationUrl || '#'} target="_blank" rel="noopener noreferrer">{campaignDetails?.creativeDetails[0]?.destinationUrl}</Link></div></div>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>

            <PricingPlanModal isOpen={showPricingPlanModal} onClose={() => setShowPricingPlanModal(false)} returnUrl={window.location.origin + "/admin/artist-manager"} />
            <CreditPlanModal isOpen={showCreditPlanModal} onClose={() => setShowCreditPlanModal(false)} />
        </div>
    )
}
